import { ENTER } from '@angular/cdk/keycodes';
import { Component, inject, Inject } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { mailRegex } from 'src/app/shared/constants/mailRegex.constants';
import { DisbursmentService } from 'src/app/shared/service/disbursment/disbursment.service';
import { allowOnlyLetters, allowLimitedInput, formatAddressForDisplay, trimInput, handlePasteText } from '../../master.utils';
import { ToastrService } from 'ngx-toastr';
import { PortAgentService } from 'src/app/shared/service/disbursment/port-agent.service';

@Component({
  selector: 'app-create-port-agent',
  templateUrl: './create-port-agent.component.html',
  styleUrls: ['./create-port-agent.component.scss']
})
export class CreatePortAgentComponent {
 private loader = inject(DisbursmentService);
 isLoading$ = this.loader.loading$;

  disallowNumericData = allowOnlyLetters;
  allowLimitedInput = allowLimitedInput;

  portAgentForm: FormGroup;
  isEditMode = false;
  emailList: string[] = [];
  readonly separatorKeysCodes: number[] = [ENTER];
  removable = true;
  addOnBlur = true;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<CreatePortAgentComponent>,
    @Inject(MAT_DIALOG_DATA) public existingdata: any,
    private portAgentService: PortAgentService,
    private snackBar: ToastrService
  ) {
    const data = existingdata?.rowData;
    this.isEditMode = !!data;

    this.portAgentForm = this.fb.group({
      company_id: [data?.company_id || 0],
      company_name: [data?.company_name || '', Validators.required],
      app_owning_company_id: [data?.app_owning_company_id || 0],
      company_type_id: [data?.company_type_id || 0],
      email: [],
      address: [formatAddressForDisplay(data?.address || '')],
      accountHolderName: [data?.bank_details?.beneficiary_acc_holder_name || ''],
      bank_details_id: [data?.bank_details?.bank_details_id || 0],
      bankName: [data?.bank_details?.bank_name || ''],
      accountNumber: [data?.bank_details?.current_account_number || ''],
      correspondentAccountNumber: [data?.bank_details?.correspondent_account_number || ''],
      ibanNumber: [data?.bank_details?.iban_number || ''],
      ifscCode: [data?.bank_details?.ifsc_code || ''],
      country: [data?.bank_details?.country_name || ''],
      currency: [data?.bank_details?.currency || ''],
      bankBranchAddress: [formatAddressForDisplay(data?.bank_details?.branch_address || '')],
      swiftCode: [data?.bank_details?.swift_code || ''],
      bicCode: [data?.bank_details?.bic_code || ''],
      inCode: [data?.bank_details?.inn_code || ''],
      bikCode: [data?.bank_details?.bik_code || '']
    });

    if (data?.email) {
      if (typeof data.email === 'string') {
        this.emailList = data.email.split(',').map((e: string) => e.trim()).filter(Boolean);
      } else if (Array.isArray(data.email)) {
        this.emailList = data.email.map((e: string) => e.trim()).filter(Boolean);
      } else {
        this.emailList = [];
      }
    }

  }

  //method to trim the input
  onInputTrim(controlName: string): void {
    const control = this.portAgentForm.get(controlName);
    trimInput(control);
  }

  // Method to handle chip input: validate and add email to emailList, update form control
  addEmail(event: MatChipInputEvent): void {
    const input = event.input;
    const value = (event.value || '').trim();

    if (value && this.isValidEmail(value)) {
      this.emailList.push(value);
      if (this.emailList.length > 0) {
        input.value = '';
      }
    } else if (value) {
      this.snackBar.error('Invalid email format.');
    }

    // if (input) {
    //   input.value = '';
    // }
  }

  // Method to remove the given email from emailList, update form control value,
  removeEmail(email: string): void {
    const index = this.emailList.indexOf(email);
    if (index >= 0) {
      this.emailList.splice(index, 1);
    }
  }

  // method to validate the email
  isValidEmail(email: string): boolean {
    // const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{3,}$/;
    // return emailRegex.test(email);
    return mailRegex.test(email);
  }
  // add email to chip on typing of valid email
  checkAndAutoAddEmail(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.trim();

    if (this.isValidEmail(value)) {
      if (!this.emailList.includes(value)) {
        this.emailList.push(value);
        this.portAgentForm.get('email')?.setErrors(null);
        input.value = '';
      } else {
        this.portAgentForm.get('email')?.setErrors({ customEmailError: true });
        input.value = '';
      }
    }
  };

  onPaste(event: ClipboardEvent): void {
    const value = handlePasteText(event);
    this.portAgentForm.get('company_name')?.setValue(value);
  }

  onSubmit(): void {
    if (this.emailList.length === 0) {
      this.snackBar.error('Please enter at least one valid email.');
      return;
    }

    this.portAgentForm.markAllAsTouched();

    if (!this.portAgentForm.valid) {
      this.snackBar.error('Please fill all required fields correctly.');
      return;
    }

    this.loader.showDialogLoader();

    const formValue = this.portAgentForm.value;

    // Transform form data to match API payload structure
    const payload = {
      company_id: formValue.company_id || 0,
      app_owning_company_id: formValue.app_owning_company_id || 0,
      company_type_id: formValue.company_type_id || 0,
      company_name: formValue.company_name,
      phone_number: formValue.phone_number || '',
      email: this.emailList,
      address: formValue.address || '',
      status: 'Y',
      bank_details: {
        bank_details_id: formValue.bank_details_id,
        country_name: formValue.country || null,
        beneficiary_acc_holder_name: formValue.accountHolderName || '',
        bank_name: formValue.bankName || '',
        correspondent_account_number: formValue.correspondentAccountNumber || '',
        current_account_number: formValue.accountNumber || '',
        ifsc_code: formValue.ifscCode || '',
        bik_code: formValue.bikCode || '',
        swift_code: formValue.swiftCode || '',
        bic_code: formValue.bicCode || '',
        currency: formValue.currency || '',
        inn_code: formValue.inCode || '',
        iban_number: formValue.ibanNumber || '',
        branch_address: formValue.bankBranchAddress || ''
      }
    };

    this.portAgentService.PortAgentDataToDb(payload).subscribe({
      next: (res) => {
        this.loader.hideDialogLoader();
        this.snackBar.success(res.message);
        this.dialogRef.close('refresh');
      },
      error: (err) => {
        this.loader.hideDialogLoader();
        const errorMsg = err.error?.error || 'Something went wrong. Please try again.';
        this.snackBar.error(errorMsg);
      }
    });
  }

  closeDialog(): void {
    this.dialogRef.close();
  }
}
